import { isApproved, isNotApproved, isNotConfirmed, isConfirmed } from '@sporkbytes/object-utils';
import { getValidDate, isWithinHours, isDateValid } from '@sporkbytes/time-utils';
import { isPast } from 'date-fns';
import { confirmationWindowLengthInHours, postDeliveryEditWindowInHours } from '@sporkbytes/config';
import { isAdmin } from '@sporkbytes/auth-utils';
import { itemTotalAfterDiscount } from '@sporkbytes/purchase-order-menu-item-utils';
import Money from '@sporkbytes/money';
import { makeCommaSeparatedList } from '@sporkbytes/string-utils';
import { roundCurrency, calculatePercent } from '@sporkbytes/math-utils';

function associatedMealOptionInCorrectState(mealOption) {
  var mealProposal = mealOption === null || mealOption === void 0 ? void 0 : mealOption.MealProposal;
  return isApproved(mealOption) && !isPast(getValidDate(mealProposal === null || mealProposal === void 0 ? void 0 : mealProposal.deliveryDate)) && (mealProposal === null || mealProposal === void 0 ? void 0 : mealProposal.status) === 'In Progress';
}

/**
 * @description Given `purchaseOrder`, determine if it can be approved.
 * @param {object} purchaseOrder - The purchase order, its associated meal option, and its associated meal proposal to determine if it can be approved.
 * @return {boolean} Whether the given purchase order can be approved.
 */
function canApprove(purchaseOrder) {
  return isNotApproved(purchaseOrder) && associatedMealOptionInCorrectState(purchaseOrder.MealOption);
}

/**
 * @description Given `purchaseOrder`, determine if it can be confirmed.
 * @param {object} purchaseOrder - The purchase order, its associated meal option, and its associated meal proposal to determine if it can be confirmed.
 * @return {boolean} Whether the given purchase order can be confirmed.
 */
function canConfirm(purchaseOrder) {
  var _purchaseOrder$MealOp, _purchaseOrder$MealOp2;
  return isApproved(purchaseOrder) && isNotConfirmed(purchaseOrder) && associatedMealOptionInCorrectState(purchaseOrder.MealOption) && isWithinHours((_purchaseOrder$MealOp = purchaseOrder.MealOption) === null || _purchaseOrder$MealOp === void 0 ? void 0 : (_purchaseOrder$MealOp2 = _purchaseOrder$MealOp.MealProposal) === null || _purchaseOrder$MealOp2 === void 0 ? void 0 : _purchaseOrder$MealOp2.deliveryDate, new Date(), confirmationWindowLengthInHours.partner, true);
}

/**
 * @description Given `purchaseOrder`, determine if it can be deleted.
 * @param {object} purchaseOrder - The purchase order, its associated meal option, and its associated meal proposal to determine if it can be deleted.
 * @return {boolean} Whether the given purchase order can be deleted.
 */
function canDelete(purchaseOrder) {
  var _purchaseOrder$MealOp;
  var mealProposal = (_purchaseOrder$MealOp = purchaseOrder.MealOption) === null || _purchaseOrder$MealOp === void 0 ? void 0 : _purchaseOrder$MealOp.MealProposal;
  return isNotApproved(purchaseOrder) && !isPast(getValidDate(mealProposal === null || mealProposal === void 0 ? void 0 : mealProposal.deliveryDate)) && ['In Progress', 'Draft'].includes(mealProposal === null || mealProposal === void 0 ? void 0 : mealProposal.status);
}

/**
 * @description Given `purchaseOrder`, determine if it can be disapproved.
 * @param {object} purchaseOrder - The purchase order, its associated meal option, and its associated meal proposal to determine if it can be disapproved.
 * @return {boolean} Whether the given purchase order can be disapproved.
 */
function canDisapprove(purchaseOrder) {
  return isApproved(purchaseOrder) && isNotConfirmed(purchaseOrder) && associatedMealOptionInCorrectState(purchaseOrder.MealOption);
}

/**
 * @description Given `purchaseOrder`, determine if it can be edited.
 * @param {object} purchaseOrder - The purchase order to determine if it can be edited.
 * @param {string} role - The role of the user who would like to edit the purchase order.
 * @return {boolean} Whether the given purchase order can be edited.
 */
function canEdit(purchaseOrder, role) {
  var isWithinPostDeliveryEditWindow = isWithinHours(new Date(), purchaseOrder === null || purchaseOrder === void 0 ? void 0 : purchaseOrder.pickupTime, postDeliveryEditWindowInHours, true);
  return isWithinPostDeliveryEditWindow || isAdmin(role);
}

/**
 * @description Given `purchaseOrder`, determine if it can have its confirmation undone.
 * @param {object} purchaseOrder - The purchase order, its associated meal option, and its associated meal proposal to determine if it can have its confirmation undone.
 * @return {boolean} Whether the given purchase order can have its confirmation undone.
 */
function canUndoConfirmation(purchaseOrder) {
  return isConfirmed(purchaseOrder) && associatedMealOptionInCorrectState(purchaseOrder.MealOption);
}

/**
 * @description Given a purchase order object, retrieve the included menu items.
 * @param {object} purchaseOrder - The purchase order object from which to retrieve the included menu items.
 * @return {array} The included menu items from the purchase order.
 */
function getIncludedItems(purchaseOrder) {
  return Array.isArray(purchaseOrder.PurchaseOrderMenuSections) ? purchaseOrder.PurchaseOrderMenuSections.flatMap(function (_ref) {
    var PurchaseOrderMenuItems = _ref.PurchaseOrderMenuItems;
    return PurchaseOrderMenuItems;
  }) : [];
}

/**
 * @description Given a purchase order object, determine if it has included menu items.
 * @param {object} purchaseOrder - The purchase order object with which to determine if it has included menu items.
 * @return {boolean} Whether or not the provided `purchaseOrder` has any included menu items.
 */
function hasIncludedItems(purchaseOrder) {
  return getIncludedItems(purchaseOrder).length > 0;
}

/**
 * @description Given `purchaseOrder` and an `amountType`, determine the cost to the client for the purchase order or the amount given to the partner for the purchase order.  When `paidToPartner` is the given `amountType`, the returned value is before commission or any payout adjustments.
 * @param {object} purchaseOrder - The purchase order for which we calculate the food subtotal.
 * @param {string} amountType - The amount being retrieved.  Valid options are `costToClient` and `paidToPartner`.
 * @return {number} The food subtotal amount for the purchase order, either the amount the client pays (if `amountType` is `costToClient`) or the amount given to the partner (if `amountType` is `paidToPartner`).
 */
function foodSubtotal(purchaseOrder, amountType) {
  var total = new Money(0);
  if (hasIncludedItems(purchaseOrder)) {
    var includedItems = getIncludedItems(purchaseOrder);
    includedItems.forEach(function (item) {
      if (item[amountType] != null && item.quantity > 0) {
        total = total.add(itemTotalAfterDiscount(item, amountType));
      }
    });
  }
  return +total;
}

/**
 * @description Given a `purchaseOrder` object, retrieve the included contacts from the purchase order.
 * @param {object} purchaseOrder - The purchase order object from which to retrieve the included contacts.
 * @return {array} The array of included contacts.
 */
function getIncludedContacts(purchaseOrder) {
  if (Array.isArray(purchaseOrder.PurchaseOrdersContacts)) {
    return purchaseOrder.PurchaseOrdersContacts.map(function (_ref) {
      var Contact = _ref.Contact;
      return Contact;
    });
  } else if (Array.isArray(purchaseOrder.Contacts)) {
    return purchaseOrder.Contacts;
  }
  return [];
}

/**
 * @description Given a `purchaseOrder` object, retrieve the included contacts' IDs from the purchase order.
 * @param {object} purchaseOrder - The purchase order object from which to retrieve the included contacts' IDs.
 * @return {array} The array of included contacts' IDs.
 */
function getIncludedContactIds(purchaseOrder) {
  return getIncludedContacts(purchaseOrder).map(function (contact) {
    return contact.id;
  });
}

function _typeof(o) {
  "@babel/helpers - typeof";

  return _typeof = "function" == typeof Symbol && "symbol" == typeof Symbol.iterator ? function (o) {
    return typeof o;
  } : function (o) {
    return o && "function" == typeof Symbol && o.constructor === Symbol && o !== Symbol.prototype ? "symbol" : typeof o;
  }, _typeof(o);
}

/**
 * @description Given a purchase order object, retrieve the included menu items for the menu.
 * @param {object} purchaseOrder - The purchase order object from which to retrieve the included menu items for the menu.
 * @return {array} The included menu items from the purchase order for the menu.
 */
function getIncludedItemsForMenu(purchaseOrder) {
  return getIncludedItems(purchaseOrder).filter(function (item) {
    return _typeof(item.PartnerMenuItem) === 'object' && item.PartnerMenuItem.visibleInMenu === true;
  });
}

/**
 * @description Given an array of purchase orders, get the names of the involved partners in a comma separated list.
 * @param {array} purchaseOrders - An array of purchase orders from which to retrieve the associated partner names.
 * @return {string} The names of all partners involved in `purchaseOrders` in a comma separated list.
 */
function getPartnerNames(purchaseOrders) {
  return makeCommaSeparatedList(purchaseOrders.map(function (purchaseOrder) {
    return purchaseOrder.PartnerLocation.Partner.name;
  }));
}

/**
 * @description Given a `purchaseOrder`, return if the driver has arrived at the partner location.
 * @param {object} purchaseOrder - The purchase order to check if the driver has arrived at the partner location.
 * @return {boolean} Whether or not the driver has arrived at the partner location.
 */
function hasArrivedAtPartnerLocation(purchaseOrder) {
  var arrivedAt = purchaseOrder.arrivedAt;
  return !!arrivedAt && isDateValid(arrivedAt);
}

/**
 * @description Given a `purchaseOrder`, return if the driver has left the partner location.
 * @param {object} purchaseOrder - The purchase order to check if the driver has left the partner location.
 * @return {boolean} Whether or not the driver has left the partner location.
 */
function hasLeftPartnerLocation(purchaseOrder) {
  var leftAt = purchaseOrder.leftAt;
  return !!leftAt && isDateValid(leftAt);
}

/**
 * @description Given a `purchaseOrder`, return wether or not the driver has picked up the items for the purchase order.
 * @param {object} purchaseOrder - The purchase order to check if the driver has picked up the items for.
 * @return {boolean} Whether or not the driver has picked up the items.
 */
function hasPickedUp(purchaseOrder) {
  var pickedUpAt = purchaseOrder.pickedUpAt;
  return !!pickedUpAt && isDateValid(pickedUpAt);
}

/**
 * @description Given `purchaseOrder`, determine if it has been billed or not.
 * @param {object} purchaseOrder - The purchase order to be determined as billed or not.
 * @return {boolean} Whether or not the given purchase order is billed.
 */
function isBilled(purchaseOrder) {
  return !!purchaseOrder.BillId;
}

/**
 * @description Given a `purchaseOrder`, determine Spork's commission.
 * @param {object} purchaseOrder - The purchase order for which we we calculate Spork's commission.
 * @return {number} Spork's commission for the purchase order.
 */
function sporkCommission(purchaseOrder) {
  return roundCurrency(calculatePercent(foodSubtotal(purchaseOrder, 'paidToPartner'), purchaseOrder.sporkCommissionPercent));
}

/**
 * @description Given a purchase order object or array of purchase order objects, calculate the total received by the partner for the purchase order(s).
 * @param {object|array} purchaseOrders - The purchase order object or array of purchase order objects for which we calculate the total received by the partner.
 * @return {number} The total amount of money received by the partner for the purchase order(s).
 */
function totalReceivedByPartner(purchaseOrders) {
  var payoutAdjustment = new Money(0);
  if (Array.isArray(purchaseOrders)) {
    return +purchaseOrders.flatMap(totalReceivedByPartner).reduce(function (accumulator, value) {
      return accumulator.add(value);
    }, new Money(0));
  }
  if (Array.isArray(purchaseOrders.PurchaseOrderPayoutAdjustments)) {
    payoutAdjustment = purchaseOrders.PurchaseOrderPayoutAdjustments.reduce(function (adjustmentTotal, adjustment) {
      return adjustmentTotal.add(adjustment.amount);
    }, new Money(0));
  }
  return +new Money(foodSubtotal(purchaseOrders, 'paidToPartner')).subtract(sporkCommission(purchaseOrders)).add(payoutAdjustment);
}

export { canApprove, canConfirm, canDelete, canDisapprove, canEdit, canUndoConfirmation, foodSubtotal, getIncludedContactIds, getIncludedContacts, getIncludedItems, getIncludedItemsForMenu, getPartnerNames, hasArrivedAtPartnerLocation, hasIncludedItems, hasLeftPartnerLocation, hasPickedUp, isBilled, sporkCommission, totalReceivedByPartner };
